import { useMutation } from '@tanstack/react-query';
import { findIndex, merge } from 'lodash';
import { produce } from 'immer';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { GUIDE_KEYS, useActiveWidget } from '../../queries/guides';
import { Page, StyleSheet, VimeoWidget } from '../../types';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';
import {
  editSettingsInVimeoWidget,
  editSourceInVimeoWidget,
} from '../../helpers/api/widgets/vimeo';
import { editVimeoWidgetStyleCommand } from '../../helpers/api/commands/widgets/vimeoWidgetCommands';
import { guideWriteCommandApi } from '../../helpers/api/guidelinesApi';
import { optimisticallyUpdateWidgetStyle } from './collections';

export enum VimeoSettings {
  loop = 'loop',
  autoplay = 'autoplay',
  detailsShown = 'detailsShown',
}

export type VimeoSettingsType = {
  [key in VimeoSettings]?: boolean;
};

export const useVimeoWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;
  const vimeoWidget = useActiveWidget() as VimeoWidget;

  return {
    editSource: useMutation(
      (options: { widgetSource: string }) =>
        editSourceInVimeoWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options.widgetSource,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateVimeoWidget({
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            widgetSettings: { sourceId: variables.widgetSource },
          });
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editStyle: useMutation(
      (options: { style: string }) => {
        const command = editVimeoWidgetStyleCommand({
          aggregateId: activeGuideId,
          aggregateVersion: activeGuideVersion,
          chapterId: activeChapterId,
          sectionId,
          columnIndex,
          widgetId,
          pageId: activePageId,
          widgetStyle: {
            stylesheet: options.style,
            language: 'css',
            version: 3,
          } as StyleSheet,
        });
        return guideWriteCommandApi(command);
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateWidgetStyle(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editSettings: useMutation(
      (options: VimeoSettingsType) =>
        editSettingsInVimeoWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateVimeoWidget({
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            widgetSettings: {
              settings: { ...vimeoWidget.settings, ...variables },
            },
          });
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateVimeoWidget = ({
  activeGuideId,
  activePageId,
  sectionId,
  columnIndex,
  widgetId,
  widgetSettings,
}: {
  activeGuideId: string;
  activePageId: string;
  sectionId: string;
  columnIndex: number;
  widgetId: string;
  widgetSettings: { [key: string]: unknown };
}) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );
      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        const vimeoWidget = {
          ...old.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ],
        } as VimeoWidget;

        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = merge(vimeoWidget, widgetSettings);
        });
      }
    },
  );
};
